import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import ReactApexChart from "react-apexcharts";
import { FiAlertCircle, FiDollarSign, FiFileText, FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import { HStack, Icon } from "@chakra-ui/react";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "components/Loading/Loading";
import { dashboardService } from "services/dashboardService";

const StatCard = ({ title, value, subtitle, icon: Icon, iconBg, valueColor }) => (
  <Card overflow="hidden" minHeight="112px" h="100%" w="100%">
    <CardBody>
      <Flex justify="space-between" align="flex-start" h="100%">
        <Box flex="1" minW="0">
          <Text fontSize="sm" color="gray.500" fontWeight="medium" mb="1">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={valueColor}>
            {value}
          </Text>
          {subtitle && (
            <Box fontSize="xs" color="gray.500" mt="1" lineHeight="tight">
              {typeof subtitle === "string" ? (
                <Text noOfLines={2} title={subtitle}>
                  {subtitle}
                </Text>
              ) : (
                subtitle
              )}
            </Box>
          )}
        </Box>
        <Box
          bg={iconBg}
          color="white"
          p="3"
          borderRadius="xl"
          fontSize="2xl"
          lineHeight="1"
          flexShrink={0}
        >
          {Icon && <Icon size={28} />}
        </Box>
      </Flex>
    </CardBody>
  </Card>
);

function Statistics() {
  const cardBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  const [dashboardData, setDashboardData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    let cancelled = false;
    dashboardService
      .getStatistics()
      .then((data) => {
        if (!cancelled) setDashboardData(data);
      })
      .catch(() => {
        if (!cancelled) setDashboardData({
          infracoes_no_mes: 0,
          valor_multas_mes_atual: 0,
          valor_multas_mes_anterior: 0,
          percentual_variacao_multas: null,
          periodo_comparacao: null,
          valor_gerado_mes_anterior: 0,
          valor_recebido_mes_anterior: 0,
          proc_ic_total: 0, proc_ic_abertos: 0, proc_ic_julgados: 0, proc_ic_deferidos: 0, proc_ic_indeferidos: 0,
          proc_na_total: 0, proc_na_abertos: 0, proc_na_julgados: 0, proc_na_deferidos: 0, proc_na_indeferidos: 0,
          proc_np_total: 0, proc_np_abertos: 0, proc_np_julgados: 0, proc_np_deferidos: 0, proc_np_indeferidos: 0,
          alertas_abertos: 0, alertas_pendentes: 0, alertas_resolvidos: 0,
          processos_pendentes_recebimento_ic: 0,
          processos_pendentes_recebimento_na: 0,
          processos_pendentes_recebimento_np: 0,
          meses_referencia_infracoes_ultimo_ano: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
          quantidade_infracoes_ultimo_ano_validadas: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          quantidade_infracoes_ultimo_ano_nao_validadas: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          valor_multas_geradas_ultimo_ano: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          valor_multas_recebidas_ultimo_ano: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false);
      });
    return () => { cancelled = true; };
  }, []);

  const formatMultasValue = (val) => {
    if (val == null) return "R$ 0,00";
    const num = typeof val === "number" ? val : parseFloat(val);
    if (Number.isNaN(num)) return "R$ 0,00";
    return "R$ " + num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCurrencyBRL = (val) => {
    if (val == null) return "R$ 0,00";
    const num = typeof val === "number" ? val : parseFloat(val);
    if (Number.isNaN(num)) return "R$ 0,00";
    return "R$ " + num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const percentual = dashboardData?.percentual_variacao_multas;
  const multasSubtitle = useMemo(() => {
    const pct = percentual;
    if (pct == null) return "Dados do mês corrente";
    const isNegative = pct < 0;
    const isPositive = pct > 0;
    const pctColor = isNegative ? "red.500" : isPositive ? "green.500" : "gray.500";
    const pctValue = Math.abs(pct).toFixed(1).replace(".", ",");
    const pctText = `${isPositive ? "+" : ""}${pctValue}% em relação ao período`;
    const TrendIcon = isNegative ? FiTrendingDown : isPositive ? FiTrendingUp : null;
    return (
      <HStack spacing="1" as="span" color={pctColor} fontWeight="medium">
        {TrendIcon && <Icon as={TrendIcon} boxSize="3.5" />}
        <Text as="span">{pctText}</Text>
      </HStack>
    );
  }, [percentual]);

  const statCards = useMemo(
    () => [
      {
        title: "Infrações Lavradas",
        value: String(dashboardData?.infracoes_no_mes ?? 0),
        subtitle: "Dados do mês corrente",
        icon: FiAlertCircle,
        iconBg: "blue.500",
      },
      {
        title: "Valor das multas no mês",
        value: formatMultasValue(dashboardData?.valor_multas_mes_atual),
        subtitle: multasSubtitle,
        icon: FiDollarSign,
        iconBg: "green.500",
      },
      {
        title: "Valor Gerado",
        value: formatMultasValue(dashboardData?.valor_gerado_mes_anterior),
        subtitle: "Valor gerado no mês anterior (data infração)",
        icon: FiDollarSign,
        iconBg: "blue.600",
      },
      {
        title: "Valor Recebido",
        value: formatMultasValue(dashboardData?.valor_recebido_mes_anterior),
        subtitle: "Valor pago no mês anterior (data pagamento)",
        icon: FiTrendingUp,
        iconBg: "green.500",
      },
      {
        title: "Proc. de IC Abertos",
        value: `${String(dashboardData?.proc_ic_abertos ?? 0)} / ${dashboardData?.proc_ic_total ?? 0}`,
        subtitle: `${dashboardData?.proc_ic_julgados ?? 0} julgados · ${dashboardData?.proc_ic_deferidos ?? 0} Def. · ${dashboardData?.proc_ic_indeferidos ?? 0} Indef.`,
        icon: FiFileText,
        iconBg: "orange.500",
      },
      {
        title: "Proc. de Defesa Abertos",
        value: `${String(dashboardData?.proc_na_abertos ?? 0)} / ${String(dashboardData?.proc_na_total ?? 0)}`,
        subtitle: `${dashboardData?.proc_na_julgados ?? 0} julgados · ${dashboardData?.proc_na_deferidos ?? 0} Def. · ${dashboardData?.proc_na_indeferidos ?? 0} Indef.`,
        icon: FiFileText,
        iconBg: "orange.500",
      },
      {
        title: "Proc. JARI Abertos",
        value: `${String(dashboardData?.proc_np_abertos ?? 0)} / ${String(dashboardData?.proc_np_total ?? 0)}`,
        subtitle: `${dashboardData?.proc_np_julgados ?? 0} julgados · ${dashboardData?.proc_np_deferidos ?? 0} Def. · ${dashboardData?.proc_np_indeferidos ?? 0} Indef.`,
        icon: FiFileText,
        iconBg: "orange.500",
      },
      {
        title: "Alertas Abertos",
        value: String(dashboardData?.alertas_abertos ?? 0),
        subtitle: `${dashboardData?.alertas_pendentes ?? 0} pendentes, ${dashboardData?.alertas_resolvidos ?? 0} resolvidos`,
        icon: FiAlertCircle,
        iconBg: "red.500",
        valueColor: "red.500",
      },
    ],
    [dashboardData, multasSubtitle]
  );

  const graficoQuantidadeInfracoesUltimoAno = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
      },
      colors: ["#3182CE", "#E53E3E"],
      dataLabels: { enabled: false },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.1,
        },
      },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: dashboardData?.meses_referencia_infracoes_ultimo_ano ?? ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
        labels: { style: { colors: textColor } },
      },
      yaxis: {
        labels: { style: { colors: textColor } },
      },
      grid: {
        borderColor: borderColor,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      tooltip: {
        theme: "dark",
      },
    }),
    [dashboardData, textColor, borderColor]
  );

  const graficoQuantidadeInfracoesUltimoAnoSeries = useMemo(
    () => [
      {
        name: "Válidas",
        data: dashboardData?.quantidade_infracoes_ultimo_ano_validadas ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "Não Válidas",
        data: dashboardData?.quantidade_infracoes_ultimo_ano_nao_validadas ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
    ],
    [dashboardData]
  );

  const graficoAnualMultasGeradasEPagas = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      colors: ["#3182CE", "#38A169"],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: dashboardData?.meses_referencia_infracoes_ultimo_ano ?? ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
        labels: { style: { colors: textColor } },
      },
      yaxis: {
        labels: {
          style: { colors: textColor },
          formatter: (value) => formatCurrencyBRL(value),
        },
      },
      grid: {
        borderColor: borderColor,
      },
      legend: {
        position: "top",
        labels: { colors: textColor },
      },
      tooltip: {
        theme: "dark",
        y: {
          formatter: (value) => formatCurrencyBRL(value),
        },
      },
    }),
    [dashboardData, textColor, borderColor]
  );

  const graficoAnualMultasGeradasEPagasSeries = useMemo(
    () => [
      {
        name: "Multas geradas",
        data: (dashboardData?.valor_multas_geradas_ultimo_ano ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]).map((value) => {
          const num = typeof value === "number" ? value : parseFloat(value);
          return Number.isNaN(num) ? 0 : num;
        }),
      },
      {
        name: "Multas recebidas",
        data: (dashboardData?.valor_multas_recebidas_ultimo_ano ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]).map((value) => {
          const num = typeof value === "number" ? value : parseFloat(value);
          return Number.isNaN(num) ? 0 : num;
        }),
      },
    ],
    [dashboardData]
  );

  const graficoProcessosPendentesRecebimentoOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      colors: ["#E53E3E"],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: ["IC", "NA", "NP"],
        labels: { style: { colors: textColor } },
      },
      yaxis: {
        labels: { style: { colors: textColor } },
      },
      grid: {
        borderColor: borderColor,
      },
      legend: { show: false },
      tooltip: {
        theme: "dark",
      },
    }),
    [textColor, borderColor]
  );

  const graficoProcessosPendentesRecebimentoSeries = useMemo(
    () => [{
      name: "Quantidade",
      data: [
        dashboardData?.processos_pendentes_recebimento_ic ?? 0,
        dashboardData?.processos_pendentes_recebimento_na ?? 0,
        dashboardData?.processos_pendentes_recebimento_np ?? 0,
      ],
    }],
    [dashboardData]
  );

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }} px="30px" pb="30px">
      {loadingStats ? (
        <Card bg={cardBg} overflow="hidden" w="100%">
          <CardBody>
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          </CardBody>
        </Card>
      ) : (
        <>
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
            gap="4"
            mb="4"
          >
            {statCards.map((card, idx) => (
              <GridItem key={idx} display="flex">
                <StatCard
                  title={card.title}
                  value={card.value}
                  subtitle={card.subtitle}
                  icon={card.icon}
                  iconBg={card.iconBg}
                  valueColor={card.valueColor}
                />
              </GridItem>
            ))}
          </Grid>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 2fr" }} gap="6">
            <GridItem>
              <Card bg={cardBg} overflow="hidden" h="100%">
                <Box p={4}>
                  <ListPageCardHeader
                    align="center"
                    title="Processos Pendentes de Recebimento"
                    subtitle="Criados e sem recebimento. Ideal é 0 (zero)."
                    bg={cardBg}
                  />
                </Box>
                <CardBody pt="0">
                  <Box h="280px">
                    <ReactApexChart
                      options={graficoProcessosPendentesRecebimentoOptions}
                      series={graficoProcessosPendentesRecebimentoSeries}
                      type="bar"
                      height="100%"
                      width="100%"
                    />
                  </Box>
                </CardBody>
              </Card>
            </GridItem>
            <GridItem>
              <Card bg={cardBg} overflow="hidden">
                <Box p={4}>
                  <ListPageCardHeader
                    align="center"
                    title="Infrações nos últimos 12 meses"
                    subtitle="Comparação entre infrações válidas e não válidas"
                    bg={cardBg}
                  />
                </Box>
                <CardBody pt="0">
                  <Box h="350px">
                    <ReactApexChart
                      options={graficoQuantidadeInfracoesUltimoAno}
                      series={graficoQuantidadeInfracoesUltimoAnoSeries}
                      type="area"
                      height="100%"
                      width="100%"
                    />
                  </Box>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          <Card bg={cardBg} overflow="hidden">
            <Box p={4}>
              <ListPageCardHeader
                align="center"
                title="Multas geradas e recebidas nos últimos 12 meses"
                subtitle="Comparação entre multas geradas e recebidas"
                bg={cardBg}
              />
            </Box>
            <CardBody pt="0">
              <Box h="350px">
                <ReactApexChart
                  options={graficoAnualMultasGeradasEPagas}
                  series={graficoAnualMultasGeradasEPagasSeries}
                  type="bar"
                  height="100%"
                  width="100%"
                />
              </Box>
            </CardBody>
          </Card>
        </>
      )}
    </Flex>
  );
}

export default Statistics;
